import React, { useState } from "react";
import {
  Box,
  TextField,
  Chip,
  Typography,
  InputAdornment,
  IconButton,
  Tooltip
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";

const TagField = ({
  label,
  value = [],
  onChange,
  placeholder = "Введите текст и нажмите Enter",
  helperText,
  maxTags = 20,
  color = "primary",
  size = "small",
  disabled = false,
  allowDuplicates = false
}) => {
  const [currentInput, setCurrentInput] = useState('');

  // Обработчик нажатия Enter
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && currentInput.trim()) {
      event.preventDefault();
      addTag(currentInput.trim());
    }
  };

  // Добавление тега
  const addTag = (newTag) => {
    if (!newTag) return;
    
    // Проверка на дубликаты (если не разрешены)
    if (!allowDuplicates && value.includes(newTag)) {
      setCurrentInput('');
      return;
    }

    // Проверка на максимальное количество тегов
    if (value.length >= maxTags) {
      setCurrentInput('');
      return;
    }

    const newTags = [...value, newTag];
    onChange(newTags);
    setCurrentInput('');
  };

  // Удаление тега
  const handleDeleteTag = (tagToDelete) => {
    const newTags = value.filter(tag => tag !== tagToDelete);
    onChange(newTags);
  };

  // Обработчик клика на кнопку добавления
  const handleAddClick = () => {
    if (currentInput.trim()) {
      addTag(currentInput.trim());
    }
  };

  return (
    <Box>
      {/* Поле ввода */}
      <TextField
        fullWidth
        label={label}
        value={currentInput}
        onChange={(e) => setCurrentInput(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        helperText={helperText}
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LocalOfferIcon color="action" fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: currentInput.trim() && (
            <InputAdornment position="end">
              <Tooltip title="Добавить тег">
                <IconButton
                  onClick={handleAddClick}
                  size="small"
                  color={color}
                  disabled={disabled || value.length >= maxTags}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          )
        }}
        sx={{
          '& .MuiInputBase-input': {
            fontSize: '0.95rem'
          }
        }}
      />
      
      {/* Счетчик тегов */}
      {value.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {value.length} из {maxTags} тегов
        </Typography>
      )}

      {/* Отображение тегов */}
      {value.length > 0 && (
        <Box 
          sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 0.8, 
            mt: 1.5,
            p: 1,
            backgroundColor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200'
          }}
        >
          {value.map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              onDelete={disabled ? undefined : () => handleDeleteTag(tag)}
              color={color}
              variant="outlined"
              size={size}
              sx={{
                backgroundColor: color === 'primary' ? '#e3f2fd' : 
                                color === 'secondary' ? '#f3e5f5' :
                                color === 'success' ? '#e8f5e8' : '#fff3e0',
                transition: 'all 0.2s',
                '&:hover': { 
                  backgroundColor: color === 'primary' ? '#bbdefb' : 
                                  color === 'secondary' ? '#e1bee7' :
                                  color === 'success' ? '#c8e6c9' : '#ffcc02',
                  transform: 'translateY(-1px)',
                  boxShadow: 1
                },
                '& .MuiChip-deleteIcon': {
                  fontSize: '18px'
                }
              }}
            />
          ))}
        </Box>
      )}

      {/* Подсказка когда нет тегов */}
      {value.length === 0 && (
        <Box 
          sx={{ 
            mt: 1,
            p: 2,
            textAlign: 'center',
            backgroundColor: 'grey.50',
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'grey.300'
          }}
        >
          <LocalOfferIcon sx={{ fontSize: 24, color: 'text.disabled', mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            Теги не добавлены. Введите текст и нажмите Enter для создания тега.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TagField;
